import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'dark-icon';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'dark-icon') {
    return (
      <Link
        to="/"
        className="group block rounded-2xl overflow-hidden cursor-pointer transition-transform duration-300 hover:-translate-y-1"
      >
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800 mb-2.5 whitespace-nowrap">
          {article.category}
        </span>
        <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2 line-clamp-3 leading-snug group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <div className="flex items-center gap-3 text-foreground-400 text-xs">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-xs"></i>
            </div>
            {article.viewCount >= 10000
              ? `${(article.viewCount / 10000).toFixed(0)}万`
              : article.viewCount}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-chat-3-line text-xs"></i>
            </div>
            {article.commentCount >= 10000
              ? `${(article.commentCount / 10000).toFixed(0)}万`
              : article.commentCount}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className="group block rounded-2xl overflow-hidden cursor-pointer transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-bold bg-primary-500 text-white flex items-center gap-1 whitespace-nowrap">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-fire-line text-xs"></i>
            </div>
            HOT
          </span>
        )}
        {article.isNew && !article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-bold bg-accent-500 text-background-50 whitespace-nowrap">
            NEW
          </span>
        )}
      </div>
      <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 mb-2.5 whitespace-nowrap">
        {article.category}
      </span>
      <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2 line-clamp-3 leading-snug group-hover:text-primary-500 transition-colors">
        {article.title}
      </h3>
      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-3">
        {article.excerpt}
      </p>
      <div className="flex items-center gap-3 text-foreground-400 text-xs">
        <span>{article.publishDate}</span>
        <span className="flex items-center gap-1">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-eye-line text-xs"></i>
          </div>
          {article.viewCount >= 10000
            ? `${(article.viewCount / 10000).toFixed(0)}万`
            : article.viewCount}
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-chat-3-line text-xs"></i>
          </div>
          {article.commentCount >= 10000
            ? `${(article.commentCount / 10000).toFixed(0)}万`
            : article.commentCount}
        </span>
      </div>
    </Link>
  );
}